import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Dropdown, Flex, MenuProps } from 'antd';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { FC, useCallback, useMemo, useRef } from 'react';
import * as Icon from 'react-feather';
import { useFormContext, UseFormSetValue } from 'react-hook-form';
import { ApiErrorResponse } from '../../common/ApiClient';
import {
  DropdownItem,
  DropdownUpload,
  Modal,
  Padding,
  Popconfirm,
  ProgrammaticUpload,
  ProgrammaticUploadRef,
  Tooltip,
} from '../../common/components';
import {
  excelErrorExists,
  excelTranslations,
  getExcelErrorMessage,
} from '../../common/utils';
import { GetApiErrorMessage } from '../../common/utils/ValidationMessagesMap';
import { useMutateAlternativesUpload } from '../query/useMutateAlternativesUpload';
import { useMutateDataObjectUpload } from '../query/useMutateDataObjectUpload';
import { useMutateKeysUpload } from '../query/useMutateKeysUpload';
import { useMutateValidationRulesUpload } from '../query/useMutateValidationRulesUpload';
import {
  AlternativeAlgorithmSetDataType,
  RequiredKeyDataType,
  ValidationRuleFormDataType,
} from '../schema';
import translations from '../translations/DataObjectUpload.translations.json';
import validationTranslations from '../translations/validation.translations.json';
import { DataObjectFormDataType, DataObjectType } from '../types';
import { mergeRequiredKeys, mergeUploaded } from './mergeUploaded';
import { AlgorithmsUpload } from './AlgorithmsUpload';
import { OnErrorHandler, OnProgressHandler } from './types';

const { addTranslations, useTranslation } = i18nNsFactory('data-object-upload');
addTranslations(translations);
addTranslations(excelTranslations);
addTranslations(validationTranslations);

export interface DataObjectUploadProps {
  id?: number;
  isInput: boolean;
  isReport: boolean;
  isDraft: boolean;
  isIntermediate: boolean;
  onError?: OnErrorHandler;
  onProgress?: OnProgressHandler;
  mode?: 'edit' | 'clone' | 'create';
  dataObjectType: DataObjectType;
}

export const DataObjectUpload: FC<DataObjectUploadProps> = ({
  id,
  isInput,
  isReport,
  isDraft,
  onError,
  onProgress,
  mode,
  dataObjectType,
  isIntermediate,
}) => {
  const { t } = useTranslation();

  const getErrorMessage: GetApiErrorMessage = useCallback(
    (code, meta) => {
      if (excelErrorExists(code)) {
        return getExcelErrorMessage(code, meta);
      }

      const options: Record<string | number, string | undefined> = {};
      if (meta?.args) {
        Object.entries(meta.args).forEach(([key, value]) => {
          options[key] = escapeSlashes(value);
        });
      }

      return t(`alert.error.code.${code}`, options) as string;
    },
    [t],
  );

  const handleUploadError = useCallback(
    (error: ApiErrorResponse) => {
      onError?.(error.response?.data.errors ?? [], getErrorMessage);
    },
    [getErrorMessage, onError],
  );

  const handleUpload = useDataObjectUpload(
    id,
    dataObjectType,
    handleUploadError,
    onError,
    onProgress,
  );
  const handleRulesUpload = useValidationRulesUpload(
    id,
    handleUploadError,
    onError,
    onProgress,
  );
  const handleAlternativesUpload = useAlternativesUpload(
    id,
    handleUploadError,
    onError,
    onProgress,
  );
  const handleKeysUpload = useKeysUpload(
    id,
    handleUploadError,
    onError,
    onProgress,
  );
  const uploadMainRef = useRef<ProgrammaticUploadRef | null>(null);

  const uploadOptions: MenuProps['items'] = useMemo(() => {
    const columns = [];
    if (isDraft) {
      columns.push({
        label:
          mode === 'create' ? (
            <DropdownUpload
              id="upload-dataObject"
              customRequest={handleUpload}
              showUploadList={false}
            >
              {t('upload.main.label')}
            </DropdownUpload>
          ) : (
            <>
              <ProgrammaticUpload
                id="dataObject-upload"
                ref={uploadMainRef}
                customRequest={handleUpload}
                showUploadList={false}
              />
              <Popconfirm
                id="confirm-upload-dataObject"
                title={t('title')}
                description={t('description')}
                okText={t('button.yes')}
                cancelText={t('button.no')}
                onConfirm={() => {
                  uploadMainRef.current?.startUpload();
                }}
              >
                <DropdownItem>{t('upload.main.label')}</DropdownItem>
              </Popconfirm>
            </>
          ),
        key: '1',
      });
    }
    if (!isInput && !isIntermediate) {
      columns.push({
        label: (
          <DropdownUpload
            id="dataObject-validationRules-upload"
            customRequest={handleRulesUpload}
            disabled={!id}
            showUploadList={false}
          >
            <Tooltip
              title={
                id
                  ? undefined
                  : t(`upload.rules.disabled.${isInput ? 'input' : 'output'}`)
              }
            >
              {t('upload.rules.label')}
            </Tooltip>
          </DropdownUpload>
        ),
        key: '2',
      });
    }

    columns.push({
      label: (
        <AlgorithmsUpload
          id={id}
          onError={handleUploadError}
          onProgress={onProgress}
          isInput={isInput}
        />
      ),
      key: '3',
    });

    if (!isInput) {
      columns.push({
        label: (
          <DropdownUpload
            id="dataObject-alternatives-upload"
            customRequest={handleAlternativesUpload}
            disabled={!id}
            showUploadList={false}
          >
            <Tooltip
              title={
                id
                  ? undefined
                  : t(
                      `upload.alternatives.disabled.${isInput ? 'input' : 'output'}`,
                    )
              }
            >
              {t('upload.alternatives.label')}
            </Tooltip>
          </DropdownUpload>
        ),
        key: '4',
      });

      if (isReport) {
        columns.push({
          label: (
            <DropdownUpload
              id="dataObject-keys-upload"
              customRequest={handleKeysUpload}
              disabled={!id}
              showUploadList={false}
            >
              <Tooltip
                title={
                  id
                    ? undefined
                    : t(`upload.keys.disabled.${isInput ? 'input' : 'output'}`)
                }
              >
                {t('upload.keys.label')}
              </Tooltip>
            </DropdownUpload>
          ),
          key: '5',
        });
      }
    }

    return columns;
  }, [
    handleAlternativesUpload,
    handleKeysUpload,
    handleRulesUpload,
    handleUpload,
    handleUploadError,
    id,
    isInput,
    isReport,
    mode,
    onProgress,
    t,
    isDraft,
    isIntermediate,
  ]);

  return (
    <Dropdown menu={{ items: uploadOptions }} placement="bottom">
      <Button id="upload-dataObject" icon={<Icon.Upload size={19} />} />
    </Dropdown>
  );
};

function mergeAlternativeSets(
  uploaded: AlternativeAlgorithmSetDataType[],
  current: AlternativeAlgorithmSetDataType[],
) {
  return mergeUploaded(uploaded, current, 'name');
}

function mergeValidationRules(
  uploadedRules: ValidationRuleFormDataType[],
  currentRules: ValidationRuleFormDataType[],
) {
  return mergeUploaded(uploadedRules, currentRules, 'code');
}

function escapeSlashes(value?: string) {
  return value?.replace(/\//g, ' \u{2215} ');
}

function useDataObjectUpload(
  id: number | undefined,
  expectedType: DataObjectType,
  handleUploadError: (error: ApiErrorResponse) => void,
  onError?: OnErrorHandler,
  onProgress?: OnProgressHandler,
) {
  const { reset, getValues } = useFormContext<DataObjectFormDataType>();
  const { mutate } = useMutateDataObjectUpload(onProgress);

  const handleUploadSuccess = useCallback(
    (uploaded: Partial<DataObjectFormDataType>) => {
      const current = getValues();
      onError?.([]);
      reset({
        ...uploaded,
        dataObjectId: current.dataObjectId,
        execution: current.execution,
        dataSources: current.dataSources,
        statusCode: current.statusCode,
        validationRules: current.validationRules,
        versionId: current.versionId,
        versionNumber: current.versionNumber,
        dimensions: uploaded.dimensions || [],
      });
    },
    [getValues, onError, reset],
  );

  return useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { file, id, expectedType },
        {
          onError: handleUploadError,
          onSuccess: handleUploadSuccess,
        },
      );
    },
    [mutate, id, expectedType, handleUploadError, handleUploadSuccess],
  );
}

interface UploadConfirmFooterProps<T> {
  currentValue: T;
  merge: (upoaded: T, current: T) => T;
  prop: keyof DataObjectFormDataType;
  setValue: UseFormSetValue<DataObjectFormDataType>;
  uploaded: T;
  replace?: (uploaded: T) => T;
}

function UploadConfirmFooter<T>({
  currentValue,
  setValue,
  uploaded,
  merge,
  prop,
  replace,
}: UploadConfirmFooterProps<T>) {
  const { t } = useTranslation();

  return (
    <Padding top>
      <Flex justify="end" gap={8}>
        <Button
          id="cancel-partial-upload"
          type="default"
          onClick={Modal.destroyAll}
        >
          {t('button.cancel')}
        </Button>
        <Button
          id="merge-uploaded"
          type="primary"
          onClick={() => {
            setValue(
              prop,
              merge(uploaded, currentValue) as keyof DataObjectFormDataType,
              { shouldDirty: true },
            );
            Modal.destroyAll();
          }}
        >
          {t('button.modify')}
        </Button>
        <Button
          id="replace-uploaded"
          type="primary"
          onClick={() => {
            setValue(
              prop,
              (replace
                ? replace(uploaded)
                : uploaded) as keyof DataObjectFormDataType,
              { shouldDirty: true },
            );
            Modal.destroyAll();
          }}
        >
          {t('button.replace')}
        </Button>
      </Flex>
    </Padding>
  );
}

function useValidationRulesUpload(
  id: number | undefined,
  handleUploadError: (error: ApiErrorResponse) => void,
  onError?: OnErrorHandler,
  onProgress?: OnProgressHandler,
) {
  const { mutate } = useMutateValidationRulesUpload(onProgress);
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<DataObjectFormDataType>();

  const handleUploadSuccess = useCallback(
    (uploaded: ValidationRuleFormDataType[]) => {
      onError?.([]);
      Modal.confirm({
        title: t('rules.upload-confirmation'),
        footer: (
          <UploadConfirmFooter
            currentValue={[...getValues('validationRules')]}
            merge={mergeValidationRules}
            prop={'validationRules'}
            setValue={setValue}
            uploaded={uploaded}
          />
        ),
      });
    },
    [getValues, onError, setValue, t],
  );

  return useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { file, id },
        {
          onError: handleUploadError,
          onSuccess: handleUploadSuccess,
        },
      );
    },
    [id, mutate, handleUploadError, handleUploadSuccess],
  );
}

function useAlternativesUpload(
  id: number | undefined,
  handleUploadError: (error: ApiErrorResponse) => void,
  onError?: OnErrorHandler,
  onProgress?: OnProgressHandler,
) {
  const { mutate } = useMutateAlternativesUpload(onProgress);
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<DataObjectFormDataType>();

  const handleUploadSuccess = useCallback(
    (uploaded: AlternativeAlgorithmSetDataType[]) => {
      onError?.([]);
      Modal.confirm({
        title: t('alternatives.upload-confirmation'),
        footer: (
          <UploadConfirmFooter
            currentValue={[...getValues('alternativeAlgorithmSets')]}
            merge={mergeAlternativeSets}
            prop={'alternativeAlgorithmSets'}
            setValue={setValue}
            uploaded={uploaded}
          />
        ),
      });
    },
    [getValues, onError, setValue, t],
  );

  return useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { file, id },
        {
          onError: handleUploadError,
          onSuccess: handleUploadSuccess,
        },
      );
    },
    [id, mutate, handleUploadError, handleUploadSuccess],
  );
}

function useKeysUpload(
  id: number | undefined,
  handleUploadError: (error: ApiErrorResponse) => void,
  onError?: OnErrorHandler,
  onProgress?: OnProgressHandler,
) {
  const { mutate } = useMutateKeysUpload(onProgress);
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<DataObjectFormDataType>();

  const handleUploadSuccess = useCallback(
    (uploaded: RequiredKeyDataType[]) => {
      onError?.([]);
      Modal.confirm({
        title: t('keys.upload-confirmation'),
        footer: (
          <UploadConfirmFooter
            currentValue={[...getValues('requiredKeys')]}
            merge={mergeRequiredKeys}
            replace={(keys) => mergeRequiredKeys(keys, [])}
            prop={'requiredKeys'}
            setValue={setValue}
            uploaded={uploaded}
          />
        ),
      });
    },
    [getValues, onError, setValue, t],
  );

  return useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { file, id },
        {
          onError: handleUploadError,
          onSuccess: handleUploadSuccess,
        },
      );
    },
    [id, mutate, handleUploadError, handleUploadSuccess],
  );
}
